import { InterviewItem } from "../../types/Interview";

// Quão bem o agente de vídeo vai conseguir julgar este item.
//
// Deliberadamente qualitativo: exibir um percentual daria ao professor a
// impressão de que algo foi medido. Nada foi. O que vale é o MOTIVO — é nele
// que ele age.
//
// Calibrada contra os 20 itens dos checklists 66 e 67 (gerados pela entrevista,
// todos bem formados -> 20 "forte", zero falso positivo) e contra 7 casos
// adversariais escritos à mão -> 7 pegos. Uma heurística que reclama de tudo
// vira ruído e o professor para de olhar; a barra é alta de propósito.
//
// A AUTORIDADE É O SERVIDOR: as mesmas sete regras vivem no validador de
// Lacunas em PHP (família D) e é ele que barra a Aplicação, porque é o único
// lado que pode. Esta cópia existe para o sinal aparecer no mesmo quadro em
// que o professor lê o Item, sem esperar o próximo turno. Divergiu? A do
// servidor vence, e o bug é desta.
export type NivelDeteccao = "forte" | "revisar" | "fraca";

export const rotuloContagemDeteccao = (quantidade: number, grupo: "forte" | "revisar") => {
  if (quantidade === 1) return `1 ${ROTULOS[grupo]}`;
  return grupo === "forte" ? `${quantidade} detecções fortes` : `${quantidade} detecções a revisar`;
};

export interface Deteccao {
  nivel: NivelDeteccao;
  motivo: string;
}

const VAZIO = /^\s*(n[ãa]o se aplica|n\/a|-)?\s*$/i;

// Ação que a câmera ou o microfone conseguem registrar. Lista ampla de
// propósito: o objetivo NÃO é exigir um verbo da lista, é detectar a ausência
// de qualquer ação concreta quando a regra já é subjetiva (ver REGRA 6).
const OBSERVAVEL =
  /\b(diz|fala|verbaliz|pergunt|question|menciona|cita|nomeia|explica|informa|comunica|descreve|orienta|apresenta|cumpriment|acolhe|valida|reconhec|oferece|cheg|olha|aponta|toca|palpa|ausculta|percut|posicion|senta|agacha|levanta|higieniz|lava|calça|veste|utiliza|entrega|escreve|flexion|estende|segura|aproxima|afasta|realiza|executa|faz|adota|mant[eé]m|garante|investiga|avalia|solicita|identifica|aciona|verifica|checa|confirma|aguarda|responde|prepara|inicia|conclui|silenci|paus)/i;

// Vocabulário visual complementar. Não aceita “demonstra adequadamente”
// sozinho: deve haver evidência visual concreta, inclusive comportamento
// descrito como substantivo. Não condiciona o foco a execução manual.
const OBSERVAVEL_FOCAL = /\b(inspe[çc][ãa]o|inspecion|manuse|ilumin|friccion|fric[çc][ãa]o|introduz|inser[çc]|inseri|conect|desconect|pression|comprim|compress[ãa]o|aferi|afer[eiç]|medir|mede|medi[çc][ãa]o|sorri|sorris|acena|acen[oa]|gesticul|gesto|express[ãõ][oe]s?\s+facial|express[ãõ][oe]s?\s+faciais|contato\s+visual|dire[çc][ãa]o\s+do\s+olhar|orienta[çc][ãa]o\s+corporal|postura\s+corporal)/i;

// Qualificadores que descrevem um julgamento, não um fato observável.
// Sozinhos, deixam o agente sem base — e o professor sem defesa quando o
// aluno contestar a nota.
const SUBJETIVO =
  /\b(adequad|apropriad|corretament|satisfat|empati|profissionalis|acolhedor|humaniz|respeitos|educad|cordial|clara?ment|bem\b|boa\b|bom\b)/i;

// Verbos que OBSERVAVEL aceita mas que, num passo, descrevem julgamento em vez
// de ação registrável. Espelha DetectionGrader::VAGO_EM_PASSO — só se aplica a
// passos, nunca a `correct_when`, cuja calibração não se mexe.
const VAGO_EM_PASSO =
  /^\s*(avalia|faz|realiza|executa|verifica|checa|garante|mant[eé]m|adota|conclui)\b/i;

const limpo = (v?: string) => (v || "").trim();
const ausente = (v?: string) => VAZIO.test(limpo(v));

// A extensão focal orienta a autoria na interface; a calibração padrão acima
// continua espelhando o servidor para as regras da câmera geral.
export function avaliarDeteccao(item: InterviewItem, cameraFocal = false): Deteccao {
  const d = item.detection || {};
  const correto = limpo(d.correct_when);
  const falha = limpo(d.not_done_when);
  const observavel = (texto: string) => OBSERVAVEL.test(texto) || (cameraFocal && OBSERVAVEL_FOCAL.test(texto));

  if (ausente(d.speech) && ausente(d.video)) {
    return {
      nivel: "fraca",
      motivo: cameraFocal ? "sem regra de vídeo — a IA não sabe o que procurar" : "sem regra de fala nem de vídeo — a IA não sabe o que procurar",
    };
  }
  if (ausente(d.correct_when)) {
    return {
      nivel: "fraca",
      motivo: "sem “correto quando” — a IA não tem critério para aprovar",
    };
  }
  if (correto.length < 40) {
    return {
      nivel: "revisar",
      motivo:
        "regra curta demais para distinguir execução correta de incorreta",
    };
  }
  if (ausente(d.not_done_when)) {
    return {
      nivel: "revisar",
      motivo:
        "sem “não realizou quando” — a IA vai inferir o oposto, e inferência erra",
    };
  }
  if (item.critical && falha.length < 80) {
    return {
      nivel: "revisar",
      motivo:
        "item eliminatório com regra rasa — detalhe o que caracteriza a falha",
    };
  }
  if (SUBJETIVO.test(correto) && !observavel(correto)) {
    return {
      nivel: "revisar",
      motivo:
        cameraFocal ? "regra subjetiva sem ação observável — descreva o que a câmera focal deve mostrar" : "regra subjetiva sem ação observável — descreva o que se vê ou se ouve",
    };
  }
  if (item.question.length > 140) {
    return {
      nivel: "revisar",
      motivo:
        "enunciado longo — provavelmente soma ações que falham separadamente",
    };
  }

  // REGRAS 8 e 9 (ADR-0003) — sobre os passos, quando o Item os declara.
  //
  // Só avaliam Item QUE TEM passos: a ausência não é defeito, senão a ADR-0003
  // viraria migração obrigatória de todo Checklist existente. A autoridade
  // continua no servidor (DetectionGrader::vagueStep / partialOrder); esta
  // cópia existe para o sinal aparecer no mesmo quadro em que o professor lê.
  const passos = item.detection?.steps ?? [];
  if (passos.length > 0) {
    const vago = passos.find((p) => {
      const t = limpo(typeof p === "string" ? p : p?.text);
      return (
        t.length < 12 ||
        (SUBJETIVO.test(t) && !observavel(t)) ||
        // «avalia adequadamente» casa SUBJETIVO e OBSERVAVEL ao mesmo tempo,
        // porque `avalia` está na lista ampla — a regra 6 não pega. Espelha
        // DetectionGrader::VAGO_EM_PASSO; a autoridade é o servidor.
        (VAGO_EM_PASSO.test(t) && SUBJETIVO.test(t))
      );
    });
    if (vago) {
      return {
        nivel: "revisar",
        motivo:
          "passo vago demais para ser observado — diga o que a câmera precisa ver",
      };
    }

    if (passos.length > 1) {
      const comOrdem = passos.filter(
        (p) => typeof p !== "string" && typeof p?.order === "number",
      ).length;
      if (comOrdem > 0 && comOrdem < passos.length) {
        return {
          nivel: "revisar",
          motivo:
            "ordem declarada pela metade — ordene todos os passos ou nenhum",
        };
      }
    }
  }

  return {
    nivel: "forte",
    motivo:
      passos.length > 0
        ? `${cameraFocal ? "vídeo" : "fala, vídeo"} e ${passos.length} etapas obrigatórias definidas`
        : `${cameraFocal ? "vídeo" : "fala, vídeo"} e critérios objetivos definidos`,
  };
}

export const CORES: Record<NivelDeteccao, "success" | "warning" | "error"> = {
  forte: "success",
  revisar: "warning",
  fraca: "error",
};

export const ROTULOS: Record<NivelDeteccao, string> = {
  forte: "detecção forte",
  revisar: "detecção a revisar",
  fraca: "detecção fraca",
};
